import AccessGroupDetails from '@/views/AccessGroupDetails.vue';
import AccountSettings from '@/views/AccountSettings.vue';
import AccountSpaceRedirect from '@/views/AccountSpaceRedirect.vue';
import AiChat from '@/views/AiChat.vue';
import DashboardDetails from '@/views/DashboardDetails.vue';
import Dashboards from '@/views/Dashboards.vue';
import DataSourceDetails from '@/views/DataSourceDetails.vue';
import GoalCycles from '@/views/GoalCycles.vue';
import GoalDetails from '@/views/GoalDetails.vue';
import GoalInsights from '@/views/GoalInsights.vue';
import GoalSettings from '@/views/GoalSettings.vue';
import GoalStatusSettings from '@/views/GoalStatusSettings.vue';
import GoalTypes from '@/views/GoalTypes.vue';
import GoalsExplorer from '@/views/GoalsExplorer.vue';
import Home from '@/views/Home.vue';
import IdentityAndProvisioning from '@/views/IdentityAndProvisioning.vue';
import IntegrationSettings from '@/views/IntegrationSettings.vue';
import Logout from '@/components/Logout.vue';
import MSTeamsAccountSpaceRedirect from '@/views/MSTeamsAccountSpaceRedirect.vue';
import MSTeamsConnect from '@/views/MSTeamsConnect.vue';
import MSTeamsTeamDetails from '@/views/MSTeamsTeamDetails.vue';
import MyNotifications from '@/views/MyNotifications.vue';
import NotFound from '@/views/NotFound.vue';
import OAuthCodeGrantRedirect from '@/views/OAuthCodeGrantRedirect.vue';
import OAuthRedirect from '@/lib/oauth-redirect';
import Onboarding from '@/views/Onboarding.vue';
import PasswordForget from '@/views/PasswordForget.vue';
import PlanningDetails from '@/views/PlanningDetails.vue';
import Plannings from '@/views/Plannings.vue';
import Plans from '@/views/Plans.vue';
import Profile from '@/views/Profile.vue';
import ProfileAccess from '@/views/ProfileAccess.vue';
import ProfileSettings from '@/views/ProfileSettings.vue';
import PropertiesView from '@/views/PropertiesView.vue';
import PublishedViewWrapper from '@/views/PublishedViewWrapper.vue';
import Register from '@/views/Register.vue';
import SamlChooseAccount from '@/views/SamlChooseAccount.vue';
import ScheduledReports from '@/views/ScheduledReports.vue';
import SetPassword from '@/views/SetPassword.vue';
import SlackConnect from '@/views/SlackConnect.vue';
import SpaceDetails from '@/views/SpaceDetails.vue';
import SpaceList from '@/views/SpaceList.vue';
import UpdateDetails from '@/views/UpdateDetails.vue';
import UpdateFeedExplorer from '@/views/UpdateFeedExplorer.vue';
import UpdateScheduleDetails from '@/views/UpdateScheduleDetails.vue';
import UpdatesList from '@/views/UpdatesList.vue';
import UpdatesListOthers from '@/views/UpdatesListOthers.vue';
import UserList from '@/views/UserList.vue';
import i18n from '@/lang';
import msauth from '@/lib/msauth';
import {
  ACCESS_GROUP_LIST,
  ACCOUNT_SETTINGS,
  AI_CHAT,
  GOALS_EXPLORER,
  GOAL_DETAILS,
  GOAL_INSIGHTS,
  HOME,
  PROFILE,
  PROFILE_SETTINGS,
  SPACE_DETAILS, UPDATES_EXPLORER,
  USER_FIELDS_NO_SELECTION, USER_LIST,
} from '@/route-names';
import { EVENTS, legacyRoutePrefix } from '@/lib/constants';
import { EventBus } from '@/lib/event-bus';
import { GOAL_INSIGHTS_HEALTH_VIEW, GOAL_INSIGHTS_PERFORMANCE_VIEW } from '@/route-params';
import { MAIN_LAYOUT, PLAIN, SETTINGS_LAYOUT } from '@/lib/layout';
import { createRouter, createWebHashHistory } from 'vue-router';
import { datadogRum } from '@datadog/browser-rum';
import { experimentFlag } from 'shared/experiments.json';
import { featureFlag, moduleFlag, routeName } from 'shared/constants.json';
import { isEmpty } from 'shared/lib/object/object';
import { routeNameToAccess } from '@/route-access';
import { shouldShowFeatureGate, showFeatureGate } from '@/lib/feature-gate';
import { showOnboarding } from '@/lib/onboarding/onboarding';

export const authenticatedRoutes = [];

export const isAuthenticatedRouteName = (name) => authenticatedRoutes.some((r) => r.name === name);

export const setDocumentTitle = (meta) => {
  if (isEmpty(meta?.title)) {
    return;
  }
  document.title = `${meta.title()} | Mooncamp`;
};

export default function initRouter(store) {
  authenticatedRoutes.push(
    {
      path: 'home',
      name: HOME,
      component: Home,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('views.home'),
      },
    },
    {
      path: 'ai-chat/:chatId?',
      name: AI_CHAT,
      component: AiChat,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('views.aiChat'),
        featureGateFlag: experimentFlag.aiChat,
        featureGate: (store, to, from, featureGateFlag) => {
          if (!store.state.isLoggedIn || !shouldShowFeatureGate(featureGateFlag, store)) {
            return true;
          }
          return { name: HOME };
        },
      },
    },
    {
      path: 'profiles/:userId',
      props: true,
      name: PROFILE,
      component: Profile,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('views.profile'),
      },
    },
    {
      path: 'account',
      name: routeName.accountSpace,
      component: AccountSpaceRedirect,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('views.accountSpace'),
      },
    },
    {
      path: 'space/:optionId',
      name: SPACE_DETAILS,
      component: SpaceDetails,
      props: true,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('views.spaceDetails'),
      },
    },
    {
      path: 'dashboards/overview',
      name: routeName.dashboards,
      component: Dashboards,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('views.dashboards'),
      },
    },
    {
      path: 'dashboards/:pageId',
      name: routeName.dashboardDetails,
      component: DashboardDetails,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('views.dashboardDetails'),
      },
    },
    {
      path: 'dashboards/reports',
      name: routeName.scheduledReports,
      component: ScheduledReports,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('dashboards.reports'),
      },
    },
    {
      path: 'dashboards/goal-insights',
      name: GOAL_INSIGHTS,
      component: GoalInsights,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('views.goalInsights'),
        flags: [moduleFlag.goals],
        accessGroupFlags: routeNameToAccess(GOAL_INSIGHTS),
        featureGateFlag: featureFlag.healthDashboard,
        featureGate: (store, to, from, featureGateFlag) => {
          if (to.query.view === undefined) {
            return {
              ...to,
              query: {
                ...to.query,
                view: GOAL_INSIGHTS_PERFORMANCE_VIEW,
              },
            };
          }
          if (to.query.view === GOAL_INSIGHTS_HEALTH_VIEW) {
            if (!shouldShowFeatureGate(featureGateFlag, store)) {
              return true;
            }
            if (to.query.print !== undefined) {
              return true;
            }
            if (from.name === undefined) {
              return from;
            }
            return !showFeatureGate(featureGateFlag, store);
          }
          return true;
        },
      },
    },
    {
      path: 'check-ins',
      name: UPDATES_EXPLORER,
      component: UpdatesList,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('navigation.updatesExplorer'),
        flags: [moduleFlag.updates],
      },
    },
    {
      path: 'check-in-schedules',
      name: routeName.updateSchedules,
      component: UpdatesListOthers,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('navigation.updateSchedules'),
        flags: [moduleFlag.updates],
      },
    },
    {
      path: 'check-in-schedules/:notificationId',
      name: routeName.updateScheduleDetails,
      component: UpdateScheduleDetails,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('navigation.updateScheduleDetails'),
        flags: [moduleFlag.updates],
      },
    },
    {
      path: 'check-in-templates',
      name: routeName.updateTemplates,
      component: UpdatesListOthers,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('navigation.updateTemplates'),
        flags: [moduleFlag.updates],
      },
    },
    {
      path: 'check-ins/:id',
      name: routeName.updateDetails,
      component: UpdateDetails,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('navigation.updatesExplorer'),
        flags: [moduleFlag.updates],
      },
    },
    {
      path: 'msteams/team-okrs',
      name: routeName.teamsTeamOkrs,
      component: MSTeamsTeamDetails,
      meta: {
        layout: PLAIN,
        title: () => '',
        flags: [moduleFlag.goals],
      },
    },
    {
      path: 'msteams/account-okrs',
      name: routeName.teamsAccountOkrs,
      component: MSTeamsAccountSpaceRedirect,
      meta: {
        layout: PLAIN,
        title: () => '',
        flags: [moduleFlag.goals],
      },
    },
    {
      path: 'goals',
      name: GOALS_EXPLORER,
      component: GoalsExplorer,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => {
          const goalSettings = Object.values(store.state.nebula.goalSettings);
          if (goalSettings.length === 0 || goalSettings[0].featureNamePlural === '') {
            return i18n.t('goalSettings.featureName.defaultPlural');
          }
          return goalSettings[0].featureNamePlural;
        },
        flags: [moduleFlag.goals],
      },
    },
    {
      path: 'plans',
      name: routeName.planningList,
      component: Plannings,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('navigation.plannings'),
        flags: [moduleFlag.plannings],
      },
    },
    {
      path: 'plans/:id',
      name: routeName.planningDetails,
      component: PlanningDetails,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('navigation.planningDetails'),
        flags: [moduleFlag.plannings],
      },
    },
    {
      path: 'goals/:goalId',
      name: GOAL_DETAILS,
      component: GoalDetails,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('views.goalDetails'),
        flags: [moduleFlag.goals],
      },
    },
    {
      path: 'goals-feed',
      name: routeName.goalUpdateFeed,
      component: UpdateFeedExplorer,
      meta: {
        layout: MAIN_LAYOUT,
        title: () => i18n.t('views.goalsUpdateFeed'),
        flags: [moduleFlag.goals],
      },
    },
    {
      path: 'users',
      name: USER_LIST,
      component: UserList,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.userList'),
        accessGroupFlags: routeNameToAccess(USER_LIST),
      },
    },
    {
      path: 'teams',
      name: routeName.spaceList,
      component: SpaceList,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.spaceList'),
        accessGroupFlags: routeNameToAccess(routeName.spaceList),
      },
    },
    {
      path: 'access-groups',
      name: ACCESS_GROUP_LIST,
      component: AccessGroupDetails,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.accessGroupDetails'),
        accessGroupFlags: routeNameToAccess(ACCESS_GROUP_LIST),
      },
    },
    {
      path: 'properties',
      name: USER_FIELDS_NO_SELECTION,
      component: PropertiesView,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.properties'),
        accessGroupFlags: routeNameToAccess(USER_FIELDS_NO_SELECTION),
      },
    },
    {
      path: 'profile-settings',
      name: PROFILE_SETTINGS,
      component: ProfileSettings,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.profileSettings'),
      },
    },
    {
      path: 'profile-access',
      name: routeName.profileAccess,
      component: ProfileAccess,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.profileAccess'),
      },
    },
    {
      path: 'my-notifications',
      name: routeName.myNotifications,
      component: MyNotifications,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.myNotifications'),
      },
    },
    {
      path: 'identity-and-provisioning',
      name: routeName.identityAndProvisioning,
      component: IdentityAndProvisioning,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('settingsNavigation.identityAndProvisioning'),
        accessGroupFlags: routeNameToAccess(routeName.identityAndProvisioning),
      },
    },
    {
      path: 'account-settings',
      name: ACCOUNT_SETTINGS,
      component: AccountSettings,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.accountSettings'),
        accessGroupFlags: routeNameToAccess(ACCOUNT_SETTINGS),
      },
    },
    {
      path: 'billing',
      name: routeName.plans,
      component: Plans,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.plans'),
        accessGroupFlags: routeNameToAccess(routeName.plans),
      },
    },
    {
      path: 'integration-settings',
      name: routeName.integrationSettings,
      component: IntegrationSettings,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.integrationSettings'),
        accessGroupFlags: routeNameToAccess(routeName.integrationSettings),
      },
    },
    {
      path: 'data-source/:type',
      name: routeName.dataSourceDetails,
      component: DataSourceDetails,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.manageDataSource'),
      },
    },
    {
      path: 'goal/settings',
      name: routeName.goalSettings,
      component: GoalSettings,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.goalSettings'),
        accessGroupFlags: routeNameToAccess(routeName.goalSettings),
      },
    },
    {
      path: 'goal/cycles',
      name: routeName.goalCycles,
      component: GoalCycles,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.goalCycles'),
        accessGroupFlags: routeNameToAccess(routeName.goalCycles),
      },
    },
    {
      path: 'goal/types/:optionId?',
      name: routeName.goalTypes,
      component: GoalTypes,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.goalTypes'),
        accessGroupFlags: routeNameToAccess(routeName.goalTypes),
      },
    },
    {
      path: 'goal/status',
      name: routeName.goalStatusSettings,
      component: GoalStatusSettings,
      meta: {
        layout: SETTINGS_LAYOUT,
        title: () => i18n.t('views.goalStatus'),
        accessGroupFlags: routeNameToAccess(routeName.goalStatusSettings),
      },
    },
  );

  const router = createRouter({
    history: createWebHashHistory(),
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      }
      return { x: 0, y: 0 };
    },
    routes: [
      {
        path: '/login',
        name: routeName.logIn,
        redirect: '/',
      },
      {
        path: '/sso-login',
        name: routeName.ssoLogIn,
        redirect: '/',
      },
      {
        path: '/sso-choose',
        name: routeName.ssoChoose,
        component: SamlChooseAccount,
        meta: {
          layout: PLAIN,
          noAuth: true,
          title: () => i18n.t('samlChooseAccount.title'),
        },
      },
      {
        path: '/signup',
        name: routeName.signUp,
        component: Register,
        meta: {
          layout: PLAIN,
          noAuth: true,
          title: () => i18n.t('views.register'),
        },
      },
      {
        path: '/signup/:slug',
        redirect: (to) => `/${to.params.slug}/signup`,
      },
      {
        path: '/:slug/signup',
        name: routeName.signUpLandingPage,
        component: Register,
        meta: {
          layout: PLAIN,
          noAuth: true,
          title: () => i18n.t('views.registerLandingPage'),
        },
      },
      {
        path: '/forgot-password',
        name: routeName.forgetPassword,
        component: PasswordForget,
        meta: {
          layout: PLAIN,
          noAuth: true,
          title: () => i18n.t('views.passwordForgetTitle'),
        },
      },
      {
        path: '/set-password',
        name: routeName.setPassword,
        component: SetPassword,
        meta: {
          layout: PLAIN,
          noAuth: true,
          title: () => i18n.t('views.setPasswordTitle'),
        },
      },
      {
        path: '/msteams/auth',
        name: routeName.teamsAuth,
        component: msauth,
        meta: {
          layout: PLAIN,
          title: () => '',
          noAuth: true,
        },
      },
      {
        path: '/logout',
        name: routeName.logout,
        component: Logout,
        meta: {
          title: () => '',
          layout: PLAIN,
          noAuth: true,
        },
      },
      {
        path: '/onboarding',
        name: routeName.onboarding,
        component: Onboarding,
        meta: {
          layout: PLAIN,
          title: () => i18n.t('views.onboarding'),
        },
      },
      {
        // Not account-specific: the page hosts the account picker itself, so it lives
        // at the top level (like /onboarding) rather than under /:tenantSlug. Keeping it
        // slugless avoids the healer/guard stamping a stale account slug onto it.
        path: '/msteams-connect',
        name: routeName.msTeamsConnect,
        component: MSTeamsConnect,
        meta: {
          layout: PLAIN,
          title: () => i18n.t('msTeamsConnect.title'),
        },
      },
      {
        // Same as /msteams-connect: the page hosts its own account picker, so keep it
        // top-level and slugless rather than under /:tenantSlug.
        path: '/slack-connect',
        name: routeName.slackConnect,
        component: SlackConnect,
        meta: {
          layout: PLAIN,
          title: () => i18n.t('slackConnect.title'),
        },
      },
      {
        path: '/oauth-redirect',
        name: routeName.oauthRedirect,
        component: OAuthRedirect,
        meta: {
          title: () => '',
          layout: PLAIN,
          noAuth: true,
        },
      },
      {
        path: '/oauth2/authorize',
        name: routeName.oAuthCodeGrantFlowAuthorize,
        component: OAuthCodeGrantRedirect,
        meta: {
          title: () => i18n.t('views.authorizationCodeGrantRedirect'),
          layout: PLAIN,
          noAuth: false,
        },
      },
      {
        path: '/',
        redirect: () => {
          const slug = store.getters.loggedInUserAccount?.activeSlug?.slug;
          if (slug) {
            return { name: routeName.home, params: { tenantSlug: slug } };
          }
          return '/home';
        },
      },
      ...authenticatedRoutes.map((route) => ({
        ...route,
        name: `${legacyRoutePrefix}${route.name}`,
        path: route.path.startsWith('/') ? route.path : `/${route.path}`,
        // Ensure that for top-level routes, the redirects are absolute
        redirect: typeof route.redirect === 'string' && !route.redirect.startsWith('/')
          ? `/${route.redirect}`
          : route.redirect,
      })),
      {
        path: '/public/:savedViewId',
        name: routeName.publishedView,
        component: PublishedViewWrapper,
        meta: {
          layout: PLAIN,
          title: () => 'Goals',
          public: true,
        },
      },
      {
        path: '/:tenantSlug',
        children: authenticatedRoutes,
      },
      {
        path: '/404',
        name: routeName.notFound,
        component: NotFound,
        meta: {
          layout: PLAIN,
          title: () => i18n.t('views.notFound'),
          noAuth: true,
        },
      },
      {
        path: '/:pathMatch(.*)*',
        component: NotFound,
        meta: {
          layout: MAIN_LAYOUT,
          title: () => i18n.t('views.notFound'),
        },
      },
    ],
  });

  const enforceOnboarding = (state, getters, to) => {
    if (to.name === routeName.onboarding || to.name === routeName.publishedView) {
      return false;
    }

    if (!state.isLoggedIn) {
      return false;
    }

    return showOnboarding({ user: getters.loggedInUser, account: getters.loggedInUserAccount });
  };

  const featureGateForRouter = (store, to, from, featureGateFlag) => {
    if (!shouldShowFeatureGate(featureGateFlag, store)) {
      return true;
    }
    if (from.name === undefined) {
      return from;
    }
    return !showFeatureGate(featureGateFlag, store);
  };

  router.beforeEach((to, from, next) => {
    const { state, getters } = store;

    if (to.matched.some((record) => record.path.includes('/:tenantSlug')) && !to.params.tenantSlug) {
      const account = getters.loggedInUserAccount;
      const slug = from.params.tenantSlug || account?.activeSlug?.slug;

      if (slug) {
        next({ ...to, params: { ...to.params, tenantSlug: slug } });
        return;
      }
    }

    if (enforceOnboarding(state, getters, to)) {
      next({ name: routeName.onboarding });
      return;
    }

    if (state.isLoggedIn || to.meta.noAuth) {
      setDocumentTitle(to.meta);
    }

    if (to.meta.featureGateFlag !== undefined) {
      const featureGateFn = to.meta.featureGate !== undefined ? to.meta.featureGate : featureGateForRouter;
      // newTo can assume (true|false|RouteLocalizationNormalized)
      const newTo = featureGateFn(store, to, from, to.meta.featureGateFlag);
      if (newTo === false) {
        next(false);
        return;
      }
      if (newTo !== true) {
        next(newTo);
        return;
      }
    }

    next();
  });

  router.afterEach((to) => {
    const normalizedRouteName = typeof to.name === 'string' && to.name.startsWith(legacyRoutePrefix)
      ? to.name.slice(legacyRoutePrefix.length)
      : to.name;

    datadogRum.startView(normalizedRouteName);
    switch (normalizedRouteName) {
      case GOAL_INSIGHTS:
        EventBus.$emit(EVENTS.INSIGHTS.DASHBOARD_VISITED);
        break;
      case routeName.dashboardDetails:
        EventBus.$emit(EVENTS.INSIGHTS.CUSTOM_DASHBOARD_VISITED);
        break;
      case PROFILE_SETTINGS:
        EventBus.$emit(EVENTS.SETTINGS.PROFILE_SETTINGS_VISITED);
        break;
      default:
        break;
    }
  });

  const injectTenantSlug = (to) => {
    const { getters } = store;
    if (typeof to === 'object' && to !== null
      && to.name && (isAuthenticatedRouteName(to.name) || isAuthenticatedRouteName(`${legacyRoutePrefix}${to.name}`))
      && (!to.params || !to.params.tenantSlug)) {
      const account = getters.loggedInUserAccount;
      const slug = router.currentRoute.value.params.tenantSlug || account?.activeSlug?.slug;

      if (slug) {
        return { ...to, params: { ...(to.params || {}), tenantSlug: slug } };
      }
    }
    return to;
  };

  const originalResolve = router.resolve.bind(router);
  router.resolve = (to, currentLocation) => originalResolve(injectTenantSlug(to), currentLocation);

  const originalPush = router.push.bind(router);
  router.push = (to) => originalPush(injectTenantSlug(to));

  const originalReplace = router.replace.bind(router);
  router.replace = (to) => originalReplace(injectTenantSlug(to));

  return router;
}
